import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '../utils/logger'
import {
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Code, 
  Globe, 
  Settings,
  Search,
  RefreshCw,
  Workflow
} from 'lucide-react'
import { templateService } from '../services/templateService'
import { flowConfigService } from '../services/flowConfigService'
import { ResponseTemplate, CreateTemplateRequest, ValidationType } from '../types/template.types'
import { Loading } from '../components/common/Loading'
import toast from 'react-hot-toast'

const TemplateManagement: React.FC = () => {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<ResponseTemplate[]>([])
  const [validationTypes, setValidationTypes] = useState<ValidationType[]>([])
  const [flowConfigs, setFlowConfigs] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ResponseTemplate | null>(null)
  const [templateToDelete, setTemplateToDelete] = useState<ResponseTemplate | null>(null)

  // Formulário de criação/edição
  const [formData, setFormData] = useState<CreateTemplateRequest>({
    name: '',
    description: '',
    template: '',
    template_type: 'single',
    format: 'text',
    api_url: '',
    api_token: '',
    parent_template_id: undefined,
    input_prompt: '',
    input_validation: 'none',
    no_data_message: '',
    // Campos para controle de tentativas
    attempts_control_enabled: false,
    max_attempts_per_month: 2,
    attempts_reset_period: 'monthly',
    // Campo para integração com flow_configs
    flow_config_id: undefined
  })

  useEffect(() => {
    loadTemplates()
    loadValidationTypes()
    loadFlowConfigs()
  }, [])

  const loadTemplates = async () => {
    try {
      setIsLoading(true)
      const response = await templateService.getTemplates(1, 100)
      if (response.success) {
        setTemplates(response.data.templates)
      }
    } catch (error) {
      logger.error('Erro ao carregar templates:', error)
      toast.error('Erro ao carregar templates')
    } finally {
      setIsLoading(false)
    }
  }

  const loadValidationTypes = async () => {
    try {
      const response = await templateService.getValidationTypes()
      if (response.success) {
        setValidationTypes(response.data)
      }
    } catch (error) {
      logger.error('Erro ao carregar tipos de validação:', error)
    }
  }

  const loadFlowConfigs = async () => {
    try {
      const response = await flowConfigService.getFlowConfigs(1, 100)
      if (response.data.success) {
        setFlowConfigs(response.data.data.flow_configs)
      }
    } catch (error) {
      logger.error('Erro ao carregar fluxos:', error)
    }
  }

  const handleCreateTemplate = async () => {
    try {
      setIsCreating(true)
      const response = await templateService.createTemplate(formData)
      
      if (response.success) {
        toast.success('Template criado com sucesso!')
        setShowCreateModal(false)
        resetForm()
        loadTemplates()
      } else {
        toast.error('Erro ao criar template')
      }
    } catch (error) {
      logger.error('Erro ao criar template:', error)
      toast.error('Erro ao criar template')
    } finally {
      setIsCreating(false)
    }
  }

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return

    try {
      setIsCreating(true)
      const response = await templateService.updateTemplate({
        id: editingTemplate.id,
        ...formData
      })
      
      if (response.success) {
        toast.success('Template atualizado com sucesso!')
        setShowEditModal(false)
        resetForm()
        loadTemplates()
      } else {
        toast.error('Erro ao atualizar template')
      }
    } catch (error) {
      logger.error('Erro ao atualizar template:', error)
      toast.error('Erro ao atualizar template')
    } finally {
      setIsCreating(false)
    }
  }

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return

    try {
      const response = await templateService.deleteTemplate(templateToDelete.id)
      
      if (response.success) {
        toast.success('Template deletado com sucesso!')
        setShowDeleteModal(false)
        setTemplateToDelete(null)
        loadTemplates()
      } else {
        toast.error(response.message || 'Erro ao deletar template')
      }
    } catch (error) {
      logger.error('Erro ao deletar template:', error)
      toast.error('Erro ao deletar template')
    }
  }

  const handleEditTemplate = (template: ResponseTemplate) => {
    setEditingTemplate(template)
    setFormData({
      name: template.name,
      description: template.description || '',
      template: template.template,
      template_type: template.template_type,
      format: template.format,
      api_url: template.api_url || '',
      api_token: template.api_token || '',
      parent_template_id: template.parent_template_id,
      input_prompt: template.input_prompt || '',
      input_validation: template.input_validation || 'none',
      no_data_message: template.no_data_message || '',
      // Campos para controle de tentativas
      attempts_control_enabled: template.attempts_control_enabled || false,
      max_attempts_per_month: template.max_attempts_per_month || 2,
      attempts_reset_period: template.attempts_reset_period || 'monthly',
      flow_config_id: template.flow_config_id
    })
    setShowEditModal(true)
  }

  const handleDeleteClick = (template: ResponseTemplate) => {
    setTemplateToDelete(template)
    setShowDeleteModal(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      template: '',
      template_type: 'single',
      format: 'text',
      api_url: '',
      api_token: '',
      parent_template_id: undefined,
      input_prompt: '',
      input_validation: 'none',
      no_data_message: '',
      // Campos para controle de tentativas
      attempts_control_enabled: false,
      max_attempts_per_month: 2,
      attempts_reset_period: 'monthly',
      // Campo para integração com flow_configs
      flow_config_id: undefined
    })
    setEditingTemplate(null)
  }

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterType === 'all' || template.template_type === filterType
    return matchesSearch && matchesFilter
  })

  const getTemplateTypeIcon = (type: string) => {
    switch (type) {
      case 'single':
        return <Code className="w-4 h-4" />
      case 'list_detail':
        return <Settings className="w-4 h-4" />
      case 'custom_flow':
        return <Globe className="w-4 h-4" />
      case 'api_dynamic':
        return <Globe className="w-4 h-4" />
      default:
        return <Code className="w-4 h-4" />
    }
  }

  const getTemplateTypeLabel = (type: string) => {
    switch (type) {
      case 'single':
        return 'Simples'
      case 'list_detail':
        return 'Lista/Detalhe'
      case 'custom_flow':
        return 'Fluxo Personalizado'
      case 'api_dynamic':
        return 'API Dinâmica'
      default:
        return type
    }
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Gerenciar Templates
              </h1>
              <p className="text-gray-600">Crie e gerencie templates de resposta personalizados</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/templates/flows')}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Workflow className="w-4 h-4" />
              <span>Gerenciar Fluxos</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Novo Template</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filtros */}
        <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos os tipos</option>
                <option value="single">Simples</option>
                <option value="list_detail">Lista/Detalhe</option>
                <option value="custom_flow">Fluxo Personalizado</option>
                <option value="api_dynamic">API Dinâmica</option>
              </select>
            </div>
            <button
              onClick={loadTemplates}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Lista de Templates */}
        <div className="grid gap-4">
          {filteredTemplates.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <Code className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhum template encontrado
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || filterType !== 'all' 
                  ? 'Tente ajustar os filtros de busca'
                  : 'Crie seu primeiro template personalizado'
                }
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Criar Template
              </button>
            </div>
          ) : (
            filteredTemplates.map(template => (
              <div key={template.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getTemplateTypeIcon(template.template_type)}
                      <h3 className="text-lg font-medium text-gray-900">
                        {template.name}
                      </h3>
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        {getTemplateTypeLabel(template.template_type)}
                      </span>
                    </div>
                    {template.description && (
                      <p className="text-gray-600 mb-3">{template.description}</p>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>Formato: {template.format}</span>
                      {template.api_url && (
                        <span className="flex items-center space-x-1">
                          <Globe className="w-3 h-3" />
                          <span>API Externa</span>
                        </span>
                      )}
                      {/* Badge de controle de tentativas */}
                      {template.attempts_control_enabled ? (
                        <span className="flex items-center space-x-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">
                          🔒 {template.max_attempts_per_month || 2} tentativas/{template.attempts_reset_period === 'daily' ? 'dia' : template.attempts_reset_period === 'weekly' ? 'semana' : 'mês'}
                        </span>
                      ) : (
                        <span className="flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                          🔓 Sem limite
                        </span>
                      )}
                      <span>
                        Criado em: {new Date(template.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Editar template"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(template)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Deletar template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal de Criação/Edição */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                {showCreateModal ? 'Criar Template' : 'Editar Template'}
              </h2>
              
              <form onSubmit={(e) => {
                e.preventDefault()
                if (showCreateModal) {
                  handleCreateTemplate()
                } else {
                  handleUpdateTemplate()
                }
              }} className="space-y-4">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Consulta de Dados"
                    required
                  />
                </div>

                {/* Descrição */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Descrição do template"
                  />
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo *
                  </label>
                  <select
                    value={formData.template_type}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, template_type: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="single">Simples</option>
                    <option value="list_detail">Lista/Detalhe</option>
                    <option value="custom_flow">Fluxo Personalizado</option>
                    <option value="api_dynamic">API Dinâmica</option>
                  </select>
                </div>

                {/* Formato */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Formato
                  </label>
                  <select
                    value={formData.format}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, format: e.target.value as 'text' | 'markdown' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="text">Texto</option>
                    <option value="markdown">Markdown</option>
                  </select>
                </div>

                {/* Flow Config - Apenas para custom_flow e api_dynamic */}
                {(formData.template_type === 'custom_flow' || formData.template_type === 'api_dynamic') ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fluxo de Conversa
                    </label>
                    <select
                      value={formData.flow_config_id || ''}
                      onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, flow_config_id: e.target.value ? parseInt(e.target.value) : undefined }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Nenhum fluxo selecionado</option>
                      {flowConfigs.map((flow) => (
                        <option key={flow.id} value={flow.id}>
                          {flow.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Selecione um fluxo de conversa para este template
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-gray-600">
                          <strong>Fluxo de Conversa:</strong> Para templates do tipo <strong>{formData.template_type}</strong>, 
                          não é necessário configurar um fluxo de conversa. Este tipo de template funciona de forma independente.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template *
                  </label>
                  <textarea
                    value={formData.template}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, template: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={6}
                    placeholder="Digite o template usando variáveis como {{variavel}}"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use variáveis como {`{{nome}}`}, {`{{data}}`}, {`{{#each items}}`} para loops
                  </p>
                </div>

                {/* URL da API - Apenas para single e list_detail */}
                {formData.template_type === 'single' || formData.template_type === 'list_detail' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL da API
                    </label>
                    <input
                      type="url"
                      value={formData.api_url}
                      onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, api_url: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://exemplo.com/api/endpoint"
                    />
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          <strong>Configuração de API:</strong> Para templates do tipo <strong>{formData.template_type}</strong>, 
                          as configurações de API (URL e Token) devem ser definidas no <strong>Flow Config</strong> associado.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Token da API - Apenas para single e list_detail */}
                {formData.template_type === 'single' || formData.template_type === 'list_detail' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Token da API
                    </label>
                    <input
                      type="text"
                      value={formData.api_token}
                      onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, api_token: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Token de autenticação"
                    />
                  </div>
                ) : null}

                {/* Prompt de Entrada */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prompt de Entrada
                  </label>
                  <textarea
                    value={formData.input_prompt}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, input_prompt: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Digite o CPF/CNPJ para consultar os dados"
                  />
                </div>

                {/* Mensagem para Dados Não Encontrados */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensagem para Dados Não Encontrados
                  </label>
                  <textarea
                    value={formData.no_data_message || ''}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, no_data_message: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="❌ *Dados não encontrados*\n\nO CPF informado não foi localizado em nossa base de dados. Verifique se o número está correto e tente novamente."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Mensagem exibida quando a API não encontra dados (retorna null/vazio). Use formatação WhatsApp com *negrito* e _itálico_.
                  </p>
                </div>

                {/* Validação de Entrada */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Validação de Entrada
                  </label>
                  <select
                    value={formData.input_validation}
                    onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ ...prev, input_validation: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {validationTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label} - {type.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Controle de Tentativas */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    🔒 Controle de Tentativas
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Checkbox para habilitar controle */}
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="attempts_control_enabled"
                        checked={formData.attempts_control_enabled || false}
                        onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ 
                          ...prev, 
                          attempts_control_enabled: e.target.checked 
                        }))}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <label htmlFor="attempts_control_enabled" className="text-sm font-medium text-gray-700">
                          Habilitar controle de tentativas
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                          Limita quantas vezes um cliente pode usar este template
                        </p>
                      </div>
                    </div>

                    {/* Configurações quando habilitado */}
                    {formData.attempts_control_enabled && (
                      <div className="ml-7 space-y-4 bg-gray-50 p-4 rounded-lg">
                        {/* Máximo de tentativas */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Máximo de Tentativas
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={formData.max_attempts_per_month || 2}
                            onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ 
                              ...prev, 
                              max_attempts_per_month: parseInt(e.target.value) || 2 
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="2"
                          />
                        </div>

                        {/* Período de reset */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Período de Reset
                          </label>
                          <select
                            value={formData.attempts_reset_period || 'monthly'}
                            onChange={(e) => setFormData((prev: CreateTemplateRequest) => ({ 
                              ...prev, 
                              attempts_reset_period: e.target.value as 'monthly' | 'weekly' | 'daily' 
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="daily">Diário</option>
                            <option value="weekly">Semanal</option>
                            <option value="monthly">Mensal</option>
                          </select>
                        </div>

                        {/* Exemplos de configuração */}
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Exemplos de Configuração:</h4>
                          <ul className="text-xs text-blue-800 space-y-1">
                            <li>• <strong>Desbloqueio:</strong> 2 tentativas/mês</li>
                            <li>• <strong>Consulta:</strong> 5 tentativas/dia</li>
                            <li>• <strong>Relatório:</strong> 10 tentativas/semana</li>
                            <li>• <strong>Boletos:</strong> Sem limite (desabilitado)</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false)
                      setShowEditModal(false)
                      resetForm()
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {isCreating ? 'Salvando...' : (showCreateModal ? 'Criar' : 'Salvar')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirmar Exclusão
              </h3>
              <p className="text-gray-600 mb-6">
                Tem certeza que deseja excluir o template "{templateToDelete.name}"? 
                Esta ação não pode ser desfeita.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setTemplateToDelete(null)
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteTemplate}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  )
}

export default TemplateManagement
